import { QueryObserverBaseResult } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { FieldPath, FieldValues, useFormContext, useWatch } from 'react-hook-form'

/**
 * Resets the isTouched/isDirty state of a specific form field after data is fetched,
 * which prevents re-enables the Save button.
 *
 * Useful in component hierarchies where form data is progressively populated:
 * - Initial form is rendered with partial data
 * - Data is fetched asynchronously in child components
 *
 */
export const useResetFormFieldStateOnFetch = <Schema extends FieldValues>(
  path: FieldPath<Schema>,
  isFetched: QueryObserverBaseResult['isFetched'],
) => {
  const [shouldReset, setShouldReset] = useState<boolean>(false)

  const { resetField } = useFormContext<Schema>()
  const value = useWatch<Schema>({ name: path })

  useEffect(() => {
    if (isFetched) {
      setShouldReset(true)
    }
  }, [isFetched])

  useEffect(() => {
    if (shouldReset) {
      resetField(path, { defaultValue: value })
      setShouldReset(false)
    }
  }, [shouldReset])
}
